import type { Instrument } from '@/lib/schema/types';
import type { ObservationRecord } from '@/lib/state/types';
import { buildObservationExport } from '@/lib/export/toJson';
import { getSession } from '@/lib/auth/google';

/**
 * Browser → Lambda Function URL client.
 *
 * The Function URL is injected at build time via `VITE_SYNC_URL` (set in an
 * .env file or CI variable). If empty/unset, the SummaryView button renders
 * disabled — S3/CloudFront deploys without the sync stack still work, the
 * user just can't send submissions anywhere.
 *
 * Wire protocol (see `infra/sheets-sync-lambda/src/index.ts` for the
 * matching server handler):
 *
 *   POST <VITE_SYNC_URL>
 *   Content-Type: application/json
 *   X-FCCERS-Token: <VITE_SYNC_TOKEN>       // shared secret, required
 *   Body: ObservationExport (same shape we write to JSON exports)
 *
 * The Lambda appends one row per submission to the target Google Sheet;
 * duplicate submissions are detected by `record.id` and no-op rather than
 * creating a second row.
 */

export interface SyncResult {
  ok: boolean;
  /** Present on failure — human-readable reason. */
  error?: string;
  /** Present on success — row number in the Sheet the Lambda wrote. */
  sheetRow?: number;
  /** Present when the submission id had already been written. */
  duplicate?: boolean;
}

export function syncIsConfigured(): boolean {
  return Boolean(import.meta.env.VITE_SYNC_URL);
}

export async function syncObservation(
  instrument: Instrument,
  record: ObservationRecord,
): Promise<SyncResult> {
  const url = import.meta.env.VITE_SYNC_URL as string | undefined;
  if (!url) {
    return {
      ok: false,
      error:
        'Sync is not configured for this deployment (VITE_SYNC_URL is unset).',
    };
  }
  const session = getSession();
  if (!session) {
    return {
      ok: false,
      error: 'You are not signed in. Please sign in with Google to submit.',
    };
  }
  const payload = buildObservationExport(instrument, record);
  try {
    const resp = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.idToken}`,
      },
      body: JSON.stringify(payload),
    });
    if (!resp.ok) {
      const text = await resp.text().catch(() => '');
      return {
        ok: false,
        error:
          resp.status === 401 || resp.status === 403
            ? 'Your sign-in has expired. Sign out, sign in again and resubmit — the observation is saved on this device.'
            : `Sync failed (HTTP ${resp.status}): ${text || resp.statusText}`,
      };
    }
    const json = (await resp.json().catch(() => ({}))) as {
      sheetRow?: number;
      duplicate?: boolean;
    };
    return { ok: true, sheetRow: json.sheetRow, duplicate: json.duplicate };
  } catch {
    // fetch only throws on network/CORS failures — the message ("Failed to
    // fetch") means nothing to an observer.
    return {
      ok: false,
      error:
        "Couldn't reach the server. Check your connection and try again — the observation is saved on this device.",
    };
  }
}
